"use client";

import { useEffect, useState } from "react";
import styles from "./Slider.module.css";

/**
 * Range slider that mirrors the editor panels' "smooth drag, commit on release"
 * pattern: `onChange` fires continuously while dragging (raw value), and
 * `onCommit` fires once with the final value when the pointer is released.
 *
 * Consumers can use `formatValue` to render a unit suffix (e.g. "%") in the
 * header — pass `null` to hide the value label entirely.
 */
export default function Slider({
  label,
  value,
  defaultValue,
  onChange,
  onCommit,
  min = 0,
  max = 100,
  step = "any",
  disabled = false,
  formatValue,
  className = "",
  id,
  "aria-label": ariaLabel,
  ...inputProps
}) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    () => (isControlled ? value : defaultValue ?? min)
  );

  useEffect(() => {
    if (isControlled) setInternalValue(value);
  }, [value, isControlled]);

  const currentValue = isControlled ? value : internalValue;
  const safeValue = Number.isFinite(currentValue) ? currentValue : min;
  const range = max - min || 1;
  const fillPercent = Math.min(100, Math.max(0, ((safeValue - min) / range) * 100));

  const showValueLabel = formatValue !== null && (label || formatValue);
  const renderedValue = formatValue
    ? formatValue(safeValue)
    : `${Math.round(safeValue)}`;

  const inputClasses = [styles.input, disabled ? styles.disabled : "", className]
    .filter(Boolean)
    .join(" ");

  const emit = (next) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  return (
    <div className={styles.field}>
      {(label || showValueLabel) && (
        <div className={styles.header}>
          {label ? (
            <label htmlFor={id} className={styles.label}>
              {label}
            </label>
          ) : (
            <span />
          )}
          {showValueLabel ? (
            <span className={styles.valueLabel}>{renderedValue}</span>
          ) : null}
        </div>
      )}

      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={safeValue}
        aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
        onChange={(event) => {
          const next = event.target.valueAsNumber;
          if (Number.isFinite(next)) emit(next);
        }}
        onPointerUp={() => onCommit?.(safeValue)}
        onPointerCancel={() => onCommit?.(safeValue)}
        onKeyUp={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            onCommit?.(safeValue);
          }
        }}
        className={inputClasses}
        style={{ "--value": `${fillPercent}%` }}
        {...inputProps}
      />
    </div>
  );
}
